// Geometria konstrukcji: obroty, pozycje socketow, obrysy.
// Czysta logika - brak Plancka i DOM.

import { getDefinition, getSocket } from './catalog';
import type { PartDefinition, PartInstance, RobotBlueprint, Rotation, Vec2Like } from './types';

export interface Bounds {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
}

/** Obrot wektora o 0/90/180/270 stopni (CCW) w ukladzie siatki (os Y w gore). */
export function rotateVec(v: Vec2Like, rotation: Rotation): Vec2Like {
  switch (rotation) {
    case 0:
      return { x: v.x, y: v.y };
    case 90:
      return { x: -v.y, y: v.x };
    case 180:
      return { x: -v.x, y: -v.y };
    case 270:
      return { x: v.y, y: -v.x };
  }
}

/** Srodek obrysu czesci w ukladzie lokalnym (jednostki klockowe). */
export function footprintCenter(def: PartDefinition): Vec2Like {
  return { x: def.footprint.w / 2, y: def.footprint.h / 2 };
}

/** Pozycja socketu w ukladzie siatki (jednostki klockowe). */
export function socketWorldGrid(
  instance: PartInstance,
  def: PartDefinition,
  socketId: string,
): Vec2Like {
  const socket = getSocket(def, socketId);
  const rotated = rotateVec({ x: socket.x, y: socket.y }, instance.rotation);
  return { x: instance.gridX + rotated.x, y: instance.gridY + rotated.y };
}

/** Normalna socketu 'rigid' w ukladzie siatki, albo null dla 'axle'. */
export function socketNormalWorld(
  def: PartDefinition,
  socketId: string,
  rotation: Rotation,
): Vec2Like | null {
  const socket = getSocket(def, socketId);
  if (!socket.normal) return null;
  return rotateVec(socket.normal, rotation);
}

/** Srodki ksztaltow kolizji czesci w ukladzie siatki. */
export function shapeCentersWorldGrid(
  instance: PartInstance,
  def: PartDefinition,
): { center: Vec2Like; angleDeg: number; shape: PartDefinition['shapes'][number] }[] {
  return def.shapes.map((shape) => {
    const rotated = rotateVec({ x: shape.x, y: shape.y }, instance.rotation);
    return {
      center: { x: instance.gridX + rotated.x, y: instance.gridY + rotated.y },
      angleDeg: instance.rotation,
      shape,
    };
  });
}

/** Pole powierzchni czesci (m^2) - do wyznaczenia gestosci fixture. */
export function shapeAreaMeters2(shape: PartDefinition['shapes'][number], metersPerGrid: number): number {
  if (shape.kind === 'circle') {
    const r = shape.r * metersPerGrid;
    return Math.PI * r * r;
  }
  return shape.w * metersPerGrid * (shape.h * metersPerGrid);
}

/** Ksztalt czesci przeliczony na uklad siatki (po obrocie czesci). */
export interface PlacedGridShape {
  kind: 'box' | 'circle';
  /** Srodek w jednostkach klockowych. */
  center: Vec2Like;
  /** Dla box: polowy bokow wzgledem osi siatki. */
  halfW: number;
  halfH: number;
  /** Dla circle: promien. */
  radius: number;
}

/** Wszystkie ksztalty czesci w ukladzie siatki. Jedno zrodlo dla obrysu i walidacji. */
export function placedGridShapes(instance: PartInstance, def: PartDefinition): PlacedGridShape[] {
  return shapeCentersWorldGrid(instance, def).map((entry) => {
    const shape = entry.shape;
    if (shape.kind === 'box') {
      // Przy obrocie o 90/270 stopni szerokosc i wysokosc zamieniaja sie miejscami.
      const swap = entry.angleDeg === 90 || entry.angleDeg === 270;
      return {
        kind: 'box' as const,
        center: entry.center,
        halfW: (swap ? shape.h : shape.w) / 2,
        halfH: (swap ? shape.w : shape.h) / 2,
        radius: 0,
      };
    }
    return {
      kind: 'circle' as const,
      center: entry.center,
      halfW: shape.r,
      halfH: shape.r,
      radius: shape.r,
    };
  });
}

/** Czy dwa ksztalty na siatce zachodza na siebie (stykanie krawedzi nie liczy sie). */
export function gridShapesOverlap(
  a: PlacedGridShape,
  b: PlacedGridShape,
  eps = 1e-4,
): boolean {
  if (a.kind === 'box' && b.kind === 'box') {
    const overlapX =
      Math.min(a.center.x + a.halfW, b.center.x + b.halfW) -
      Math.max(a.center.x - a.halfW, b.center.x - b.halfW);
    const overlapY =
      Math.min(a.center.y + a.halfH, b.center.y + b.halfH) -
      Math.max(a.center.y - a.halfH, b.center.y - b.halfH);
    return overlapX > eps && overlapY > eps;
  }

  if (a.kind === 'circle' && b.kind === 'circle') {
    return Math.hypot(a.center.x - b.center.x, a.center.y - b.center.y) < a.radius + b.radius - eps;
  }

  const circle = a.kind === 'circle' ? a : b;
  const box = a.kind === 'box' ? a : b;
  const closestX = Math.min(Math.max(circle.center.x, box.center.x - box.halfW), box.center.x + box.halfW);
  const closestY = Math.min(Math.max(circle.center.y, box.center.y - box.halfH), box.center.y + box.halfH);
  return Math.hypot(circle.center.x - closestX, circle.center.y - closestY) < circle.radius - eps;
}

/**
 * Obrys czesci na siatce: suma rzeczywistych ksztaltow (nie sam obrys kratek).
 * Dzieki temu kolo duze jest otoczone swoim promieniem, a nie kwadratem 1x1.
 */
export function partBoundsGrid(instance: PartInstance, def: PartDefinition): Bounds {
  const shapes = placedGridShapes(instance, def);

  if (shapes.length === 0) {
    // Czesc bez kolizji - zostaje obrys, zeby edytor mial co zaznaczac.
    const center = footprintCenter(def);
    const rotated = rotateVec(center, instance.rotation);
    const swap = instance.rotation === 90 || instance.rotation === 270;
    const halfW = (swap ? def.footprint.h : def.footprint.w) / 2;
    const halfH = (swap ? def.footprint.w : def.footprint.h) / 2;
    return {
      minX: instance.gridX + rotated.x - halfW,
      maxX: instance.gridX + rotated.x + halfW,
      minY: instance.gridY + rotated.y - halfH,
      maxY: instance.gridY + rotated.y + halfH,
    };
  }

  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const shape of shapes) {
    minX = Math.min(minX, shape.center.x - shape.halfW);
    maxX = Math.max(maxX, shape.center.x + shape.halfW);
    minY = Math.min(minY, shape.center.y - shape.halfH);
    maxY = Math.max(maxY, shape.center.y + shape.halfH);
  }
  return { minX, minY, maxX, maxY };
}

/** Wspolny obrys calej konstrukcji na siatce. */
export function blueprintBoundsGrid(blueprint: RobotBlueprint): Bounds {
  if (blueprint.parts.length === 0) {
    throw new Error('Konstrukcja nie ma zadnych czesci');
  }
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const part of blueprint.parts) {
    const bounds = partBoundsGrid(part, getDefinition(part.typeId));
    minX = Math.min(minX, bounds.minX);
    minY = Math.min(minY, bounds.minY);
    maxX = Math.max(maxX, bounds.maxX);
    maxY = Math.max(maxY, bounds.maxY);
  }
  return { minX, minY, maxX, maxY };
}
